import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useEffect, useMemo } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--jira-gryffindor/jira-issue-view-contextual-survey';

import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { defineMessages, useIntl } from 'react-intl';

import { type JSX } from 'react/jsx-runtime';

import { Survey, type SurveyFormValues } from './components/survey';

import { type MessageTemplateContext } from '../../../index';

const messages = defineMessages({
	question: {
		id: 'post-office.jira-issue-view-contextual-survey.question',
		defaultMessage: 'We’d love to hear from you',
		description:
			'Heading of a small survey card shown in the corner of the Jira issue view, asking the user to rate a statement.',
	},
	statement: {
		id: 'post-office.jira-issue-view-contextual-survey.statement',
		defaultMessage: 'How easy is it to get work done in this view?',
		description:
			'The statement a user rates in a small survey card shown in the corner of the Jira issue view.',
	},
	textLabel: {
		id: 'post-office.jira-issue-view-contextual-survey.text-label',
		defaultMessage: 'What influenced your rating? (optional)',
		description:
			'Label for the optional free text field where a user explains their rating in the Jira issue view survey.',
	},
	textDescription: {
		id: 'post-office.jira-issue-view-contextual-survey.text-description',
		defaultMessage: 'Your feedback will be used to improve our products.',
		description:
			'Note under the optional free text field in the Jira issue view survey, telling the user how their feedback is used.',
	},
	thankYou: {
		id: 'post-office.jira-issue-view-contextual-survey.thank-you',
		defaultMessage: 'Thanks for your feedback',
		description:
			'Acknowledgement shown after the user submits the Jira issue view survey, before it closes.',
	},
	lowestScoreLabel: {
		id: 'post-office.jira-issue-view-contextual-survey.lowest-score-label',
		defaultMessage: 'Not very easy',
		description:
			'Label shown under the lowest (1) rating on the 5 point scale in the Jira issue view survey.',
	},
	highestScoreLabel: {
		id: 'post-office.jira-issue-view-contextual-survey.highest-score-label',
		defaultMessage: 'Very easy',
		description:
			'Label shown under the highest (5) rating on the 5 point scale in the Jira issue view survey.',
	},
	scaleButtonLabel: {
		id: 'post-office.jira-issue-view-contextual-survey.scale-button-label',
		defaultMessage: '{score} out of 5',
		description:
			'Accessible label for a numbered rating button (1 to 5) in the Jira issue view survey.',
	},
	submitLabel: {
		id: 'post-office.jira-issue-view-contextual-survey.submit-label',
		defaultMessage: 'Submit',
		description: 'Label for the submit button in the Jira issue view survey.',
	},
	dismissLabel: {
		id: 'post-office.jira-issue-view-contextual-survey.dismiss-label',
		defaultMessage: 'Dismiss survey',
		description: 'Accessible label for the dismiss (close) button in the Jira issue view survey.',
	},
});

type RenderProps = MessageTemplateContext & PlacementRenderProps;

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: RenderProps) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss, surveyId, isWorkItemView }: RenderProps) => {
		const intl = useIntl();
		const { messageClicked } = useMessageEvent();
		const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
		const { isSiteAdmin, subproduct, productEdition } = usePostOfficeContext();

		/**
		 * Post Office only attaches build metadata and the placement id to events, so the
		 * segmentation dimensions the survey is analysed by have to be sent explicitly.
		 * `isWorkItemView` is present only when the work item view mount passed it through, so
		 * JIV events never carry a false value.
		 */
		const surveyAttributes = useMemo(
			() => ({
				surveyId,
				isSiteAdmin,
				subproduct,
				productEdition,
				...(isWorkItemView === true ? { isWorkItemView: true as const } : {}),
			}),
			[surveyId, isSiteAdmin, subproduct, productEdition, isWorkItemView],
		);

		const question = intl.formatMessage(messages.question);
		const statement = intl.formatMessage(messages.statement);

		/**
		 * Only the segmented screen event: the placement mounts its own `MessageViewed` beside this
		 * component, which already reports the view to both lifecycle endpoints. Firing them here as
		 * well counts every view twice. Once per mount, so the empty dependency list is deliberate.
		 */
		useEffect(() => {
			if (isWorkItemView === true) {
				fireAnalyticsEvent(
					{ eventType: 'screen', name: 'jiraIssueViewContextualSurveyViewed' },
					{ isWorkItemView: true },
				);
			} else {
				fireAnalyticsEvent({ eventType: 'screen', name: 'jiraIssueViewContextualSurveyViewed' });
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const handleSubmit = useCallback(
			async ({ feedbackScore, writtenFeedback }: SurveyFormValues) => {
				void messageClicked();
				void onCtaActioned({
					interactionIdentifier: 'submitSurvey',
					additionalMetadata: isWorkItemView === true ? { isWorkItemView: true } : {},
				});

				fireAnalyticsEvent(
					{
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'jiraIssueViewContextualSurvey',
						attributes: {
							quantitativeResponse: feedbackScore,
							qualitativeResponse: writtenFeedback,
						},
					},
					surveyAttributes,
				);
			},
			[messageClicked, onCtaActioned, fireAnalyticsEvent, surveyAttributes, isWorkItemView],
		);

		/**
		 * The card closing after a response is not a dismissal, so it must not emit the dismiss
		 * analytics — otherwise every completed response is also counted as a dismissal and the
		 * response and dismiss rates both become unreadable.
		 */
		const handleClose = useCallback(() => {
			onDismiss?.();
		}, [onDismiss]);

		/**
		 * Deliberately no `messageDismissed()`, neither here nor on submit: the dismissal check
		 * denies for good, which would override the 30 day window the survey now relies on.
		 */
		const handleDismiss = useCallback(() => {
			void onClosed({
				interactionIdentifier: 'dismiss',
				additionalMetadata: isWorkItemView === true ? { isWorkItemView: true } : {},
			});
			if (isWorkItemView === true) {
				fireAnalyticsEvent(
					{
						eventType: 'ui',
						action: 'dismissed',
						actionSubject: 'jiraIssueViewContextualSurvey',
					},
					{ isWorkItemView: true },
				);
			} else {
				fireAnalyticsEvent({
					eventType: 'ui',
					action: 'dismissed',
					actionSubject: 'jiraIssueViewContextualSurvey',
				});
			}
			onDismiss?.();
		}, [onClosed, fireAnalyticsEvent, onDismiss, isWorkItemView]);

		return (
			<Survey
				question={question}
				statement={statement}
				getScaleButtonLabel={(score) => intl.formatMessage(messages.scaleButtonLabel, { score })}
				lowestScoreLabel={intl.formatMessage(messages.lowestScoreLabel)}
				highestScoreLabel={intl.formatMessage(messages.highestScoreLabel)}
				textLabel={intl.formatMessage(messages.textLabel)}
				textDescription={intl.formatMessage(messages.textDescription)}
				submitLabel={intl.formatMessage(messages.submitLabel)}
				dismissLabel={intl.formatMessage(messages.dismissLabel)}
				thankYouMessage={intl.formatMessage(messages.thankYou)}
				onSubmit={handleSubmit}
				onClose={handleClose}
				onDismiss={handleDismiss}
			/>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
